<template>
  <BaseButtonGroup gap="px">
    <BaseButton
      :color="buttonColor"
      data-testid="add-to-list-button"
      :disabled="!canCreateList"
      icon="list"
      :label="label"
      new-design
      new-icon
      type="a"
      @click="actionOptionSelected({ name: Action.ADD_TO_LIST })"
    >
      <template
        v-if="createListTooltip"
        #after
      >
        <BaseStreemTooltip>
          {{ createListTooltip }}
        </BaseStreemTooltip>
      </template>
    </BaseButton>
    <BaseButtonDropdown
      v-if="!hideDropdown"
      chevron
      close-on-click
      :color="buttonColor"
      data-testid="actions-list-button"
      :disabled="!canCreateList"
      new-design
      new-dropdown
      placement="bottom-end"
    >
      <BaseButtonList
        class="outreach-contact-actions"
        data-testid="actions-list"
        :options="actionOptions"
        @update:model-value="actionOptionSelected"
      />
    </BaseButtonDropdown>
  </BaseButtonGroup>
</template>

<script lang="ts">
export type Events = {
  action: [Action];
  activityCreated: [];
  add: [];
  refresh: [];
};

export enum Action {
  ADD_ACTIVITY = "add_activity",
  ADD_PRIVATE_DATA = "add_private_data",
  ADD_TO_LIST = "add_to_list",
  COPY_EMAILS = "copy_emails",
  CREATE_MEDIA_RELEASE = "create_media_release",
  CREATE_PITCH = "create_pitch",
  EXPORT_CONTACTS = "export_contacts",
}
</script>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { outreachFeatures } from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseButtonGroup,
  BaseButtonList,
  BaseStreemTooltip,
} from "shared/components/base";
import type { Option } from "shared/components/base/BaseButtonList";
import useClipboard from "shared/composables/useClipboard";
import type { OutreachContactId } from "shared/composables/useContact";
import useMediaRelease from "shared/composables/useMediaRelease";
import {
  CONTACT_LIST_LIMIT,
  MEDIA_RELEASE_TYPE,
  PITCH_TYPE,
} from "shared/constants";
import { safePush } from "shared/helpers/routing";
import { constantize, parseDocumentId } from "shared/helpers/string";
import ModalService from "shared/modals/ModalService";
import { MediaReleaseRecipient } from "shared/resources";

interface Props {
  buttonColor?: string;
  disable?: boolean;
  hideAddPrivateData?: boolean;
  hideCreateActivity?: boolean;
  hideDropdown?: boolean;
  label?: string;
  privateContacts?: boolean;
  selectedIds?: OutreachContactId[];
  suggestedContacts?: boolean;
}

interface ActionOption extends Option {
  name: Action;
}

const props = withDefaults(defineProps<Props>(), {
  buttonColor: "primary",
  label: () => getLocaleText("outreach_contact_actions.add_to_list_label"),
  selectedIds: () => [],
});

const emit = defineEmits<Events>();

const router = useRouter();
const safeRouterPush = safePush({ router });

const { mediaRelease, setDefaultMediaRelease } = useMediaRelease();

const exporting = ref(false);

const disableAction = computed(
  () => props.disable || exporting.value || props.selectedIds.length === 0
);

const disableMediaRelease = computed(
  () => disableAction.value || outreachFeatures.sendMediaRelease().isDisabled
);

const disableAddPrivateData = computed(
  () => disableAction.value || props.selectedIds.length !== 1
);

const tooltipText = computed(() => {
  if (!props.selectedIds.length) {
    return getLocaleText("outreach_contact_actions.select_a_contact_tooltip");
  }

  return null;
});

const createListTooltip = computed(() => {
  if (props.selectedIds.length > CONTACT_LIST_LIMIT) {
    return getLocaleText("outreach_contact_actions.select_contacts_tooltip", {
      limit: CONTACT_LIST_LIMIT,
    });
  }

  return tooltipText.value || outreachFeatures.contactLists().tooltipText;
});

const createMediaReleaseTooltip = computed(
  () => tooltipText.value || outreachFeatures.sendMediaRelease().tooltipText
);

const actionOptions = computed<ActionOption[]>(() => {
  const options: ActionOption[] = [];

  if (!props.suggestedContacts) {
    options.push({
      name: Action.CREATE_MEDIA_RELEASE,
      label: getLocaleText(
        "outreach_contact_actions.create_media_release_label"
      ),
      icon: "create",
      newIcon: true,
      disabled: disableMediaRelease.value,
      enableTooltip: disableMediaRelease.value,
      tooltipText: createMediaReleaseTooltip.value ?? "",
    });

    if (outreachFeatures.hasAiPitch()) {
      options.push({
        name: Action.CREATE_PITCH,
        label: getLocaleText("outreach_pitch.create_pitch"),
        icon: "ai-generate",
        newIcon: true,
        disabled: disableMediaRelease.value,
        enableTooltip: disableMediaRelease.value,
        tooltipText: createMediaReleaseTooltip.value ?? "",
      });
    }
  }

  if (props.suggestedContacts) {
    options.push({
      name: Action.COPY_EMAILS,
      label: getLocaleText("outreach_contact_actions.copy_emails_label"),
      icon: "copy",
      newIcon: true,
    });

    options.push({
      name: Action.EXPORT_CONTACTS,
      label: getLocaleText("outreach_contact_actions.download_csv_label"),
      icon: "download",
      newIcon: true,
    });
  } else {
    if (!props.hideCreateActivity) {
      options.push({
        name: Action.ADD_ACTIVITY,
        label: getLocaleText("outreach_contact_actions.create_activity_label"),
        icon: "activity",
        newIcon: true,
        disabled: disableAddPrivateData.value,
        enableTooltip: disableAddPrivateData.value,
        tooltipText: getLocaleText(
          "outreach_contact_actions.create_activity_tooltip"
        ),
      });
    }

    if (!props.hideAddPrivateData) {
      options.push({
        name: Action.ADD_PRIVATE_DATA,
        label: getLocaleText("outreach_contact_actions.add_private_data_label"),
        icon: "edit",
        newIcon: true,
        disabled: disableAddPrivateData.value,
        enableTooltip: disableAddPrivateData.value,
        tooltipText: getLocaleText(
          "outreach_contact_actions.add_private_data_tooltip"
        ),
      });
    }
  }

  return options;
});

const canCreateList = computed(
  () =>
    !outreachFeatures.contactLists().isDisabled &&
    !disableAction.value &&
    props.selectedIds.length <= CONTACT_LIST_LIMIT
);

function addContactsToSelectedLists() {
  ModalService.open("OutreachContactAddToListModal", {
    props: {
      contactListKeys: Array.from(props.selectedIds),
    },
  });
}

async function createMediaRelease(type = MEDIA_RELEASE_TYPE) {
  try {
    exporting.value = true;
    setDefaultMediaRelease();
    mediaRelease.value.mediaReleaseType = type;
    mediaRelease.value.personalised = type === PITCH_TYPE;

    mediaRelease.value.mediaReleaseRecipients = props.selectedIds.map((key) => {
      const { id, type } = parseDocumentId(key);

      return new MediaReleaseRecipient({
        contactType: constantize(type),
        contactId: id,
      });
    });

    await mediaRelease.value.saveResource({
      with: "mediaReleaseRecipients",
    });

    safeRouterPush({
      name: type === PITCH_TYPE ? "pitch-edit" : "media-release-edit",
      params: {
        id: mediaRelease.value.id,
      },
    });
  } catch (error) {
    const errorMessage =
      type === PITCH_TYPE
        ? getLocaleText("outreach_pitch.error_creating_pitch")
        : getLocaleText("outreach_contact_actions.create_media_release_error");

    ErrorDialog(getLocaleText(errorMessage), error);
  } finally {
    exporting.value = false;
  }
}

function addPrivateData() {
  if (!props.selectedIds.length) {
    return;
  }

  const { id, type } = parseDocumentId(props.selectedIds[0]);

  ModalService.open("OutreachContactDetailsModal", {
    props: {
      contactId: Number(id),
      contactType: type,
    },
    events: {
      saved: () => emit("refresh"),
    },
  });
}

function addActivity() {
  if (!props.selectedIds.length) {
    return;
  }

  const { id, type } = parseDocumentId(props.selectedIds[0]);

  ModalService.open("OutreachActivityModal", {
    props: {
      contactId: id,
      contactType: constantize(type),
    },
    events: {
      saved: () => emit("activityCreated"),
    },
  });
}

const { copyToClipboard } = useClipboard();

async function copyEmails() {
  try {
    const { data } = await $streemApiV1.post(
      "contact_lists_contacts/copy_emails",
      {
        params: {
          contact_keys: props.selectedIds,
        },
      }
    );

    copyToClipboard(data.join(","));
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_lists_contacts.copy_emails_error"),
      error
    );
  }
}

async function exportContacts() {
  try {
    const [endpoint, params] = props.privateContacts
      ? [
          "private_influencers/export_csv",
          { ids: props.selectedIds.map((key) => parseDocumentId(key).id) },
        ]
      : ["contacts/export_csv", { contact_keys: props.selectedIds }];

    const response = await $streemApiV1.post(endpoint, {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: response.headers["content-type"]?.toString(),
    });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `export_${new Date().getTime()}.csv`;
    link.click();
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_lists_contacts.export_contacts_error"),
      error
    );
  }
}

const actions = ref<Record<Action, () => void>>({
  [Action.ADD_TO_LIST]: addContactsToSelectedLists,
  [Action.CREATE_MEDIA_RELEASE]: createMediaRelease,
  [Action.CREATE_PITCH]: () => createMediaRelease(PITCH_TYPE),
  [Action.ADD_PRIVATE_DATA]: addPrivateData,
  [Action.ADD_ACTIVITY]: addActivity,
  [Action.COPY_EMAILS]: copyEmails,
  [Action.EXPORT_CONTACTS]: exportContacts,
});

function actionOptionSelected({ name }: { name: Action }): void {
  actions.value[name]?.();

  if (name === Action.ADD_TO_LIST) {
    emit("add");
  } else {
    emit("action", name);
  }
}
</script>
